import { normalizePhone, PHONE_REGEX } from './auth.js';
/**
 * Moteur de formulaires configurables (cahier §6, TODO phase 7, ADR-022).
 *
 * UN SEUL moteur, partagé par l'API (qui décide) et les portails (qui guident) :
 * mêmes conditions d'affichage, mêmes calculs, mêmes règles de validation.
 *
 * 🔒 Aucune exécution de code : les conditions et les calculs sont des DONNÉES
 * (arbres JSON d'opérateurs connus), jamais du JavaScript ni des chaînes évaluées.
 * Les expressions régulières sont choisies dans une liste fermée (pas de regex
 * libre : risque de ReDoS).
 */
/* ------------------------------------------------------------------ */
/* Types de champs                                                     */
/* ------------------------------------------------------------------ */
export const FIELD_TYPES = {
    text: { label: 'Texte court', group: 'Saisie' },
    textarea: { label: 'Texte long', group: 'Saisie' },
    integer: { label: 'Nombre entier', group: 'Saisie' },
    decimal: { label: 'Nombre décimal', group: 'Saisie' },
    date: { label: 'Date', group: 'Saisie' },
    phone: { label: 'Téléphone', group: 'Saisie' },
    email: { label: 'Courriel', group: 'Saisie' },
    select: { label: 'Liste déroulante', group: 'Choix' },
    radio: { label: 'Choix unique', group: 'Choix' },
    multiselect: { label: 'Choix multiples', group: 'Choix' },
    checkbox: { label: 'Case à cocher', group: 'Choix' },
    file: { label: 'Fichier', group: 'Pièces' },
    image: { label: 'Image', group: 'Pièces' },
    computed: { label: 'Champ calculé', group: 'Spécial' },
    readonly: { label: 'Lecture seule', group: 'Spécial' },
};
/**
 * « Champ conditionnel » (cahier §6.2) : n'importe quel champ — ou section —
 * portant une condition d'affichage (`visibility`). Ce n'est pas un type à part.
 */
export const CHOICE_TYPES = ['select', 'radio', 'multiselect'];
export const FILE_TYPES = ['file', 'image'];
/** Champs dont la valeur est produite par le système, jamais saisie. */
export const SYSTEM_TYPES = ['computed', 'readonly'];
/** Formats autorisés (liste fermée, motifs sûrs). */
export const FIELD_PATTERNS = {
    cni_sn: { label: 'N° de CNI sénégalaise (13 ou 14 chiffres)', regex: /^\d{13,14}$/ },
    digits: { label: 'Chiffres uniquement', regex: /^\d+$/ },
    letters: { label: 'Lettres, espaces, tirets, apostrophes', regex: /^[\p{L}][\p{L}\s'’-]*$/u },
    uppercase_code: { label: 'Code en majuscules et chiffres', regex: /^[A-Z0-9-]+$/ },
    postal_box: { label: 'Boîte postale (BP 1234)', regex: /^BP\s?\d{1,6}$/i },
};
export const FILE_FORMATS = {
    pdf: { label: 'PDF', mime: 'application/pdf' },
    jpg: { label: 'JPEG', mime: 'image/jpeg' },
    png: { label: 'PNG', mime: 'image/png' },
};
export const FILE_MAX_SIZE_MB = 10;
/** Valeurs produites par le système pour les champs en lecture seule. */
export const READONLY_SOURCES = {
    user_number: 'Matricule utilisateur',
    application_number: 'Matricule de candidature',
    competition_reference: 'Référence du concours',
    email: 'Adresse électronique du compte',
};
export const CONDITION_OPERATORS = {
    eq: 'est égal à',
    neq: 'est différent de',
    in: 'est parmi',
    gt: 'est supérieur à',
    gte: 'est supérieur ou égal à',
    lt: 'est inférieur à',
    lte: 'est inférieur ou égal à',
    filled: 'est renseigné',
};
const MAX_CONDITION_DEPTH = 6;
const isEmpty = (v) => v === undefined || v === null || v === '' || v === false || (Array.isArray(v) && v.length === 0);
/** Comparaison ordonnée : nombres, ou dates ISO (AAAA-MM-JJ) comparées comme du texte. */
function compare(a, b) {
    if (typeof a === 'number' && typeof b === 'number')
        return a - b;
    if (typeof a === 'string' &&
        typeof b === 'string' &&
        /^\d{4}-\d{2}-\d{2}/.test(a) &&
        /^\d{4}-\d{2}-\d{2}/.test(b))
        return a < b ? -1 : a > b ? 1 : 0;
    const na = Number(a);
    const nb = Number(b);
    if (a !== '' && b !== '' && Number.isFinite(na) && Number.isFinite(nb))
        return na - nb;
    return null;
}
/** Profondeur d'un arbre de conditions. */
export function conditionDepth(condition) {
    if (!condition || typeof condition !== 'object')
        return 0;
    if (condition.op === 'and' || condition.op === 'or')
        return (1 +
            Math.max(0, ...(Array.isArray(condition.conditions) ? condition.conditions.map(conditionDepth) : [])));
    if (condition.op === 'not')
        return 1 + conditionDepth(condition.condition);
    return 1;
}
/**
 * Évalue une condition sur les réponses. Opérateur inconnu ou arbre trop profond
 * → faux (jamais d'exception, jamais d'exécution de code).
 */
export function evaluateCondition(condition, answers, depth = 0) {
    if (!condition)
        return true;
    // Contrôle global AVANT évaluation : un « not » au-dessus de la limite ne doit pas inverser le refus.
    if (depth === 0 && conditionDepth(condition) > MAX_CONDITION_DEPTH + 1)
        return false;
    const value = 'field' in condition ? answers[condition.field] : undefined;
    switch (condition.op) {
        case 'always':
            return true;
        case 'filled':
            return !isEmpty(value);
        case 'eq':
            return Array.isArray(value) ? value.includes(condition.value) : value === condition.value;
        case 'neq':
            return Array.isArray(value) ? !value.includes(condition.value) : value !== condition.value;
        case 'in':
            return Array.isArray(value)
                ? value.some((v) => condition.values.includes(v))
                : condition.values.includes(value);
        case 'gt':
        case 'gte':
        case 'lt':
        case 'lte': {
            if (isEmpty(value))
                return false;
            const c = compare(value, condition.value);
            if (c === null)
                return false;
            return condition.op === 'gt'
                ? c > 0
                : condition.op === 'gte'
                    ? c >= 0
                    : condition.op === 'lt'
                        ? c < 0
                        : c <= 0;
        }
        case 'and':
            return condition.conditions.every((c) => evaluateCondition(c, answers, depth + 1));
        case 'or':
            return condition.conditions.some((c) => evaluateCondition(c, answers, depth + 1));
        case 'not':
            return !evaluateCondition(condition.condition, answers, depth + 1);
        default:
            return false;
    }
}
/** Champs référencés par une condition (pour vérifier l'ordre et l'existence). */
export function conditionFields(condition) {
    if (!condition)
        return [];
    switch (condition.op) {
        case 'and':
        case 'or':
            return condition.conditions.flatMap(conditionFields);
        case 'not':
            return conditionFields(condition.condition);
        case 'always':
            return [];
        default:
            return [condition.field];
    }
}
export const EXPRESSION_FUNCTIONS = {
    age_at: 'Âge à une date de référence',
    sum: 'Somme de champs',
    count_filled: 'Nombre de champs renseignés',
    concat: 'Assemblage de textes',
};
export function expressionFields(expr) {
    if (!expr)
        return [];
    switch (expr.fn) {
        case 'age_at':
            return [expr.field, ...(typeof expr.at === 'object' ? [expr.at.field] : [])];
        case 'sum':
        case 'count_filled':
            return expr.fields;
        case 'concat':
            return expr.parts.flatMap((p) => ('field' in p ? [p.field] : []));
        default:
            return [];
    }
}
function ageAt(birth, at) {
    const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(birth);
    const r = /^(\d{4})-(\d{2})-(\d{2})/.exec(at);
    if (!b || !r)
        return null;
    let age = Number(r[1]) - Number(b[1]);
    if (Number(r[2]) < Number(b[2]) || (r[2] === b[2] && Number(r[3]) < Number(b[3])))
        age--;
    return age >= 0 ? age : null;
}
/** Calcule la valeur d'un champ calculé. Entrée invalide → null. */
export function computeExpression(expr, answers) {
    switch (expr.fn) {
        case 'age_at': {
            const birth = answers[expr.field];
            const at = typeof expr.at === 'string' ? expr.at : answers[expr.at.field];
            return typeof birth === 'string' && typeof at === 'string' ? ageAt(birth, at) : null;
        }
        case 'sum': {
            const values = expr.fields
                .map((f) => answers[f])
                .filter((v) => typeof v === 'number');
            return values.length ? Math.round(values.reduce((s, v) => s + v, 0) * 1000) / 1000 : null;
        }
        case 'count_filled':
            return expr.fields.filter((f) => !isEmpty(answers[f])).length;
        case 'concat': {
            const text = expr.parts
                .map((p) => 'text' in p ? p.text : answers[p.field] == null ? '' : String(answers[p.field]))
                .filter(Boolean)
                .join(expr.separator ?? ' ');
            return text || null;
        }
        default:
            return null;
    }
}
export const FIELD_KEY_REGEX = /^[a-z][a-z0-9_]{1,49}$/;
export const MAX_SECTIONS = 20;
export const MAX_FIELDS = 150;
export const MAX_OPTIONS = 200;
export function allFields(schema) {
    return schema.sections.flatMap((s) => s.fields);
}
/** Clé technique dérivée d'un libellé : « Date de naissance » → date_de_naissance. */
export function slugifyKey(label) {
    const slug = label
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 50);
    return /^[a-z]/.test(slug) ? slug : `champ_${slug}`.slice(0, 50);
}
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
function checkCondition(condition, path, available, issues, depth = 0) {
    if (condition === undefined)
        return;
    const c = condition;
    if (!c || typeof c !== 'object' || typeof c.op !== 'string') {
        issues.push({ path, message: 'Condition invalide.' });
        return;
    }
    if (depth > MAX_CONDITION_DEPTH) {
        issues.push({ path, message: 'Condition trop imbriquée.' });
        return;
    }
    switch (c.op) {
        case 'always':
            return;
        case 'and':
        case 'or':
            if (!Array.isArray(c.conditions) || !c.conditions.length)
                issues.push({ path, message: 'Combinaison de conditions vide.' });
            else
                c.conditions.forEach((sub, i) => checkCondition(sub, `${path}.${i}`, available, issues, depth + 1));
            return;
        case 'not':
            checkCondition(c.condition, `${path}.not`, available, issues, depth + 1);
            return;
        case 'filled':
        case 'eq':
        case 'neq':
        case 'in':
        case 'gt':
        case 'gte':
        case 'lt':
        case 'lte': {
            const field = available.get(String(c.field));
            if (!field) {
                issues.push({
                    path,
                    message: `La condition porte sur « ${String(c.field)} », qui doit être un champ situé AVANT.`,
                });
                return;
            }
            if (c.op === 'in' && (!Array.isArray(c.values) || !c.values.length))
                issues.push({ path, message: 'Indiquez au moins une valeur.' });
            if (['eq', 'neq'].includes(c.op) && !['string', 'number', 'boolean'].includes(typeof c.value))
                issues.push({ path, message: 'Valeur de comparaison manquante.' });
            if (['gt', 'gte', 'lt', 'lte'].includes(c.op) &&
                !['integer', 'decimal', 'date', 'computed'].includes(field.type))
                issues.push({
                    path,
                    message: `« ${field.label} » ne peut pas être comparé (nombre ou date attendu).`,
                });
            const values = c.op === 'in' ? c.values : c.op === 'eq' || c.op === 'neq' ? [c.value] : [];
            if (field.options && values.length) {
                const allowed = new Set(field.options.map((o) => o.value));
                for (const v of values)
                    if (!allowed.has(String(v)))
                        issues.push({
                            path,
                            message: `« ${String(v)} » n'est pas une option de « ${field.label} ».`,
                        });
            }
            return;
        }
        default:
            issues.push({ path, message: `Opérateur inconnu « ${String(c.op)} ».` });
    }
}
/**
 * Valide la DÉFINITION d'un formulaire (constructeur → API). Garantit : clés
 * uniques et stables, types et options connus, références uniquement vers des
 * champs situés AVANT (donc aucun cycle), expressions et formats autorisés.
 */
export function validateFormSchema(input) {
    const issues = [];
    const root = input;
    if (!root || typeof root !== 'object' || !Array.isArray(root.sections))
        return {
            schema: null,
            issues: [{ path: 'sections', message: 'Le formulaire doit contenir des sections.' }],
        };
    if (root.sections.length > MAX_SECTIONS)
        issues.push({ path: 'sections', message: `${MAX_SECTIONS} sections au maximum.` });
    const seen = new Map();
    const sectionKeys = new Set();
    let count = 0;
    root.sections.forEach((section, si) => {
        const sp = `sections.${si}`;
        if (!section || typeof section !== 'object') {
            issues.push({ path: sp, message: 'Section invalide.' });
            return;
        }
        if (!FIELD_KEY_REGEX.test(String(section.key)))
            issues.push({
                path: `${sp}.key`,
                message: 'Clé de section invalide (minuscules, chiffres, _).',
            });
        else if (sectionKeys.has(section.key))
            issues.push({ path: `${sp}.key`, message: `Clé de section en double « ${section.key} ».` });
        sectionKeys.add(section.key);
        if (!String(section.title ?? '').trim())
            issues.push({ path: `${sp}.title`, message: 'Titre de section obligatoire.' });
        // La condition d'une section ne peut porter que sur les sections précédentes.
        checkCondition(section.visibility, `${sp}.visibility`, seen, issues);
        if (!Array.isArray(section.fields)) {
            issues.push({ path: `${sp}.fields`, message: 'Liste de champs manquante.' });
            return;
        }
        section.fields.forEach((field, fi) => {
            const fp = `${sp}.fields.${fi}`;
            count++;
            if (!field || typeof field !== 'object') {
                issues.push({ path: fp, message: 'Champ invalide.' });
                return;
            }
            const label = String(field.label ?? '').trim();
            if (!label)
                issues.push({ path: `${fp}.label`, message: 'Libellé obligatoire.' });
            if (label.length > 200)
                issues.push({ path: `${fp}.label`, message: 'Libellé trop long (200 caractères).' });
            if (!FIELD_KEY_REGEX.test(String(field.key)))
                issues.push({
                    path: `${fp}.key`,
                    message: `Clé technique invalide « ${String(field.key)} » (minuscules, chiffres, _).`,
                });
            else if (seen.has(field.key))
                issues.push({ path: `${fp}.key`, message: `Clé technique en double « ${field.key} ».` });
            if (!Object.hasOwn(FIELD_TYPES, field.type)) {
                issues.push({ path: `${fp}.type`, message: `Type inconnu « ${String(field.type)} ».` });
                return;
            }
            // Options
            if (CHOICE_TYPES.includes(field.type)) {
                if (!Array.isArray(field.options) || !field.options.length)
                    issues.push({ path: `${fp}.options`, message: `« ${label} » : au moins une option.` });
                else {
                    if (field.options.length > MAX_OPTIONS)
                        issues.push({ path: `${fp}.options`, message: `${MAX_OPTIONS} options au maximum.` });
                    const values = new Set();
                    field.options.forEach((o, oi) => {
                        if (!o || !String(o.value ?? '').trim() || !String(o.label ?? '').trim())
                            issues.push({
                                path: `${fp}.options.${oi}`,
                                message: `« ${label} » : option ${oi + 1} incomplète.`,
                            });
                        else if (values.has(o.value))
                            issues.push({
                                path: `${fp}.options.${oi}`,
                                message: `« ${label} » : valeur d'option en double « ${o.value} ».`,
                            });
                        values.add(o.value);
                    });
                }
            }
            else if (field.options !== undefined && field.options.length)
                issues.push({ path: `${fp}.options`, message: `« ${label} » : ce type n'a pas d'options.` });
            // Validation
            const v = field.validation ?? {};
            if (v.pattern !== undefined && !Object.hasOwn(FIELD_PATTERNS, v.pattern))
                issues.push({
                    path: `${fp}.validation.pattern`,
                    message: `Format inconnu « ${String(v.pattern)} ».`,
                });
            for (const k of ['minLength', 'maxLength', 'minItems', 'maxItems'])
                if (v[k] !== undefined && (!Number.isInteger(v[k]) || v[k] < 0))
                    issues.push({ path: `${fp}.validation.${k}`, message: 'Entier positif attendu.' });
            if (v.minLength !== undefined && v.maxLength !== undefined && v.minLength > v.maxLength)
                issues.push({
                    path: `${fp}.validation`,
                    message: `« ${label} » : longueur minimale > maximale.`,
                });
            if (v.min !== undefined && v.max !== undefined && v.min > v.max)
                issues.push({ path: `${fp}.validation`, message: `« ${label} » : minimum > maximum.` });
            for (const k of ['minDate', 'maxDate'])
                if (v[k] !== undefined && v[k] !== 'today' && !ISO_DATE.test(String(v[k])))
                    issues.push({
                        path: `${fp}.validation.${k}`,
                        message: 'Date AAAA-MM-JJ ou « today » attendue.',
                    });
            // Pièces
            if (FILE_TYPES.includes(field.type)) {
                const f = field.file;
                if (!f || !Array.isArray(f.formats) || !f.formats.length)
                    issues.push({
                        path: `${fp}.file`,
                        message: `« ${label} » : formats de fichier à préciser.`,
                    });
                else {
                    for (const format of f.formats)
                        if (!Object.hasOwn(FILE_FORMATS, format))
                            issues.push({
                                path: `${fp}.file.formats`,
                                message: `Format de fichier inconnu « ${String(format)} ».`,
                            });
                    if (field.type === 'image' && f.formats.includes('pdf'))
                        issues.push({
                            path: `${fp}.file.formats`,
                            message: `« ${label} » : une image n'accepte pas le PDF.`,
                        });
                    if (!(f.maxSizeMb > 0 && f.maxSizeMb <= FILE_MAX_SIZE_MB))
                        issues.push({
                            path: `${fp}.file.maxSizeMb`,
                            message: `Taille maximale entre 0 et ${FILE_MAX_SIZE_MB} Mo.`,
                        });
                }
            }
            // Champs système
            if (field.type === 'computed') {
                if (!field.compute || !Object.hasOwn(EXPRESSION_FUNCTIONS, field.compute.fn))
                    issues.push({ path: `${fp}.compute`, message: `« ${label} » : calcul à définir.` });
                else {
                    const refs = expressionFields(field.compute);
                    if (!refs.length)
                        issues.push({ path: `${fp}.compute`, message: `« ${label} » : aucun champ source.` });
                    for (const ref of refs)
                        if (!seen.has(ref))
                            issues.push({
                                path: `${fp}.compute`,
                                message: `« ${label} » : « ${ref} » doit être un champ situé AVANT.`,
                            });
                    if (field.compute.fn === 'age_at') {
                        const src = seen.get(field.compute.field);
                        if (src && src.type !== 'date')
                            issues.push({
                                path: `${fp}.compute`,
                                message: 'L’âge se calcule depuis un champ date.',
                            });
                        if (typeof field.compute.at === 'string' && !ISO_DATE.test(field.compute.at))
                            issues.push({
                                path: `${fp}.compute`,
                                message: 'Date de référence AAAA-MM-JJ attendue.',
                            });
                    }
                }
                if (field.required)
                    issues.push({
                        path: `${fp}.required`,
                        message: 'Un champ calculé n’est pas « obligatoire ».',
                    });
            }
            if (field.type === 'readonly') {
                if (!field.source || !Object.hasOwn(READONLY_SOURCES, field.source))
                    issues.push({
                        path: `${fp}.source`,
                        message: `« ${label} » : source de la valeur à préciser.`,
                    });
                if (field.required)
                    issues.push({
                        path: `${fp}.required`,
                        message: 'Un champ en lecture seule n’est pas « obligatoire ».',
                    });
            }
            checkCondition(field.visibility, `${fp}.visibility`, seen, issues);
            if (FIELD_KEY_REGEX.test(String(field.key)) && !seen.has(field.key))
                seen.set(field.key, field);
        });
    });
    if (count > MAX_FIELDS)
        issues.push({ path: 'sections', message: `${MAX_FIELDS} champs au maximum.` });
    if (count === 0)
        issues.push({ path: 'sections', message: 'Le formulaire ne contient aucun champ.' });
    return { schema: issues.length ? null : root, issues };
}
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function todayIso() {
    return new Date().toISOString().slice(0, 10);
}
const isFileAnswer = (v) => !!v && typeof v === 'object' && !Array.isArray(v) && typeof v.fileId === 'string';
/** Contrôle et normalise UNE valeur saisie. Renvoie [valeur, erreur]. */
function checkValue(field, raw, today) {
    const v = field.validation ?? {};
    switch (field.type) {
        case 'text':
        case 'textarea': {
            if (typeof raw !== 'string')
                return [raw, 'Texte attendu.'];
            const value = raw.trim();
            const max = v.maxLength ?? (field.type === 'text' ? 255 : 5000);
            if (v.minLength !== undefined && value.length < v.minLength)
                return [value, `Au moins ${v.minLength} caractères.`];
            if (value.length > max)
                return [value, `Au plus ${max} caractères.`];
            if (v.pattern && !FIELD_PATTERNS[v.pattern].regex.test(value))
                return [value, `Format attendu : ${FIELD_PATTERNS[v.pattern].label}.`];
            return [value, null];
        }
        case 'integer':
        case 'decimal': {
            const value = typeof raw === 'string' && raw.trim() !== '' ? Number(raw.replace(',', '.')) : raw;
            if (typeof value !== 'number' || !Number.isFinite(value))
                return [raw, 'Nombre attendu.'];
            if (field.type === 'integer' && !Number.isInteger(value))
                return [value, 'Nombre entier attendu.'];
            if (v.min !== undefined && value < v.min)
                return [value, `Valeur minimale : ${v.min}.`];
            if (v.max !== undefined && value > v.max)
                return [value, `Valeur maximale : ${v.max}.`];
            return [value, null];
        }
        case 'date': {
            if (typeof raw !== 'string' ||
                !ISO_DATE.test(raw) ||
                Number.isNaN(Date.parse(`${raw}T00:00:00Z`)))
                return [raw, 'Date invalide (AAAA-MM-JJ).'];
            const min = v.minDate === 'today' ? today : v.minDate;
            const max = v.maxDate === 'today' ? today : v.maxDate;
            if (min && raw < min)
                return [raw, `Date au plus tôt : ${min.split('-').reverse().join('/')}.`];
            if (max && raw > max)
                return [raw, `Date au plus tard : ${max.split('-').reverse().join('/')}.`];
            return [raw, null];
        }
        case 'phone': {
            if (typeof raw !== 'string')
                return [raw, 'Numéro attendu.'];
            const value = normalizePhone(raw);
            return PHONE_REGEX.test(value) ? [value, null] : [raw, 'Numéro de téléphone invalide.'];
        }
        case 'email': {
            if (typeof raw !== 'string')
                return [raw, 'Adresse attendue.'];
            const value = raw.trim().toLowerCase();
            return EMAIL_REGEX.test(value) && value.length <= 254
                ? [value, null]
                : [raw, 'Adresse électronique invalide.'];
        }
        case 'select':
        case 'radio': {
            const allowed = field.options?.map((o) => o.value) ?? [];
            return typeof raw === 'string' && allowed.includes(raw)
                ? [raw, null]
                : [raw, 'Choix non autorisé.'];
        }
        case 'multiselect': {
            const allowed = new Set(field.options?.map((o) => o.value) ?? []);
            if (!Array.isArray(raw) || !raw.every((x) => typeof x === 'string' && allowed.has(x)))
                return [raw, 'Choix non autorisé.'];
            const value = [...new Set(raw)];
            if (v.minItems !== undefined && value.length < v.minItems)
                return [value, `Au moins ${v.minItems} choix.`];
            if (v.maxItems !== undefined && value.length > v.maxItems)
                return [value, `Au plus ${v.maxItems} choix.`];
            return [value, null];
        }
        case 'checkbox':
            return typeof raw === 'boolean' ? [raw, null] : [raw, 'Case à cocher attendue.'];
        case 'file':
        case 'image':
            return isFileAnswer(raw)
                ? [
                    { fileId: raw.fileId, ...(raw.name ? { name: String(raw.name).slice(0, 200) } : {}) },
                    null,
                ]
                : [raw, 'Pièce jointe invalide.'];
        default:
            return [raw, 'Champ non modifiable.'];
    }
}
/**
 * Valide les réponses d'un candidat contre UNE version de formulaire.
 * 🔒 Clé inconnue → erreur (champ injecté) ; valeur fournie pour un champ calculé
 * ou en lecture seule → erreur ; champs masqués → valeurs ignorées ; calculs refaits.
 */
export function validateAnswers(schema, input, ctx) {
    const today = ctx.today ?? todayIso();
    const errors = {};
    const values = {};
    const missing = [];
    const visible = [];
    const known = new Map(allFields(schema).map((f) => [f.key, f]));
    if (!input || typeof input !== 'object' || Array.isArray(input)) {
        return { values, errors: { _form: 'Réponses invalides.' }, missing, visible };
    }
    for (const key of Object.keys(input)) {
        const field = known.get(key);
        if (!field)
            errors[key] = 'Champ inconnu dans cette version du formulaire.';
        else if (SYSTEM_TYPES.includes(field.type) && input[key] !== undefined && input[key] !== null)
            errors[key] = 'Champ non modifiable.';
    }
    // Parcours dans l'ordre : les conditions ne voient que des valeurs déjà validées.
    for (const section of schema.sections) {
        const sectionVisible = evaluateCondition(section.visibility, values);
        for (const field of section.fields) {
            if (!sectionVisible || !evaluateCondition(field.visibility, values))
                continue;
            visible.push(field.key);
            if (field.type === 'computed') {
                values[field.key] = field.compute ? computeExpression(field.compute, values) : null;
                continue;
            }
            if (field.type === 'readonly') {
                values[field.key] = (field.source && ctx.readonlyValues?.[field.source]) ?? null;
                continue;
            }
            if (errors[field.key])
                continue;
            const raw = input[field.key];
            if (isEmpty(raw)) {
                if (field.required) {
                    missing.push(field.key);
                    if (ctx.mode === 'submit')
                        errors[field.key] =
                            field.type === 'checkbox' ? 'Cette case doit être cochée.' : 'Champ obligatoire.';
                }
                // Case non cochée : on conserve `false` (réponse explicite).
                if (field.type === 'checkbox' && raw === false)
                    values[field.key] = false;
                continue;
            }
            const [value, error] = checkValue(field, raw, today);
            if (error)
                errors[field.key] = error;
            else
                values[field.key] = value;
        }
    }
    return { values, errors, missing, visible };
}
const DIFF_PROPS = [
    ['label', 'libellé'],
    ['type', 'type'],
    ['required', 'caractère obligatoire'],
    ['options', 'options'],
    ['validation', 'contrôles'],
    ['visibility', 'condition d’affichage'],
    ['file', 'paramètres de pièce'],
    ['compute', 'calcul'],
    ['description', 'description'],
    ['help', 'aide'],
];
/** Différences entre deux versions, champ par champ (par clé technique). */
export function diffSchemas(before, after) {
    const a = new Map(allFields(before).map((f) => [f.key, f]));
    const b = new Map(allFields(after).map((f) => [f.key, f]));
    const sectionOf = (schema) => new Map(schema.sections.flatMap((s) => s.fields.map((f) => [f.key, s.key])));
    const sa = sectionOf(before);
    const sb = sectionOf(after);
    const out = [];
    for (const [key, field] of b) {
        const old = a.get(key);
        if (!old) {
            out.push({ key, label: field.label, change: 'added', details: [] });
            continue;
        }
        const details = DIFF_PROPS.filter(([p]) => JSON.stringify(old[p] ?? null) !== JSON.stringify(field[p] ?? null)).map(([, l]) => l);
        if (sa.get(key) !== sb.get(key))
            details.push('section');
        if (details.length)
            out.push({ key, label: field.label, change: 'changed', details });
    }
    for (const [key, field] of a)
        if (!b.has(key))
            out.push({ key, label: field.label, change: 'removed', details: [] });
    return out;
}
/** Formulaire de départ proposé à la création (identité du candidat). */
export function starterSchema() {
    return {
        sections: [
            {
                key: 'identite',
                title: 'Identité',
                fields: [
                    {
                        key: 'nom',
                        type: 'text',
                        label: 'Nom',
                        required: true,
                        validation: { maxLength: 120, pattern: 'letters' },
                    },
                    {
                        key: 'prenoms',
                        type: 'text',
                        label: 'Prénom(s)',
                        required: true,
                        validation: { maxLength: 120, pattern: 'letters' },
                    },
                    {
                        key: 'date_naissance',
                        type: 'date',
                        label: 'Date de naissance',
                        required: true,
                        validation: { maxDate: 'today' },
                    },
                    { key: 'lieu_naissance', type: 'text', label: 'Lieu de naissance', required: true },
                    {
                        key: 'sexe',
                        type: 'radio',
                        label: 'Sexe',
                        required: true,
                        options: [
                            { value: 'F', label: 'Féminin' },
                            { value: 'M', label: 'Masculin' },
                        ],
                    },
                    { key: 'telephone', type: 'phone', label: 'Téléphone', required: true },
                ],
            },
        ],
    };
}
//# sourceMappingURL=forms.js.map